import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useCurrency } from '../contexts/CurrencyContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import Navbar from '../components/Navbar';
import './ItemDetails.css';

interface StoreItemPricingTier {
  storeItemPricingTierId: number;
  storeItemId: number;
  minQuantity: number;
  maxQuantity: number | null;
  unitPrice: number;
  currencyId?: number | null;
  currencyName?: string | null;
  isActive: boolean;
  displayOrder: number;
  notes?: string | null;
}

interface StoreItem {
  storeItemId: number;
  itemId?: number;
  itemName?: string;
  itemNameEn?: string;
  itemCode?: string;
  price?: number;
  currencyId?: number | null;
  currencyName?: string | null;
  stockQuantity: number;
  minStockLevel?: number;
  minimumOrderQuantity?: number;
  isExternalOrder?: boolean;
  leadTimeDays?: number;
  isAvailable?: boolean;
  images?: Array<{ 
    imageUrl?: string; 
    imagePath?: string;
    isDefault?: boolean;
    itemImageId?: number;
    imageName?: string;
  }>;
  videoUrl?: string;
  specifications?: Array<{ 
    specificationName?: string; 
    specificationValue?: string;
    itemSpecificationsId?: number;
  }>;
  description?: string;
  pricingTiers?: Array<{
    storeItemPricingTierId: number;
    storeItemId: number;
    minQuantity: number;
    maxQuantity: number | null;
    unitPrice: number;
    currencyId?: number | null;
    currencyName?: string | null;
    isActive: boolean;
    displayOrder: number;
    notes?: string | null;
  }>;
  shippingDetails?: Array<{
    storeShippingMethodId: number;
    methodNameAr: string;
    methodNameEn: string;
    price: number;
    minDays: number;
    maxDays: number;
  }>;
}

interface Item {
  id: number;
  name: string;
  nameEn?: string;
  description?: string;
  price: number;
  currencyName?: string | null;
  image: string;
  stockStatus: 'inStock' | 'limitedStock' | 'outOfStock';
  paymentOption?: 'full' | 'partial';
  minimumOrderQuantity?: number;
  isExternalOrder?: boolean;
  shippingDetails?: Array<{
    storeShippingMethodId: number;
    methodNameAr: string;
    methodNameEn: string;
    price: number;
    minDays: number;
    maxDays: number;
  }>;
}

const ItemDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [item, setItem] = useState<Item | null>(null);
  const [storeItem, setStoreItem] = useState<StoreItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageChanging, setImageChanging] = useState(false);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [pricingTiers, setPricingTiers] = useState<StoreItemPricingTier[]>([]);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);

  useEffect(() => {
    const fetchItemDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all store items and find the one with matching storeItemId
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/store/1/items?isAvailable=true`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch item details');
        }

        const storeItems: StoreItem[] = await response.json();
        
        // Find the item with matching storeItemId
        const storeItemId = parseInt(id || '0');
        const data = storeItems.find(item => item.storeItemId === storeItemId || item.itemId === storeItemId);
        
        if (!data) {
          throw new Error('Item not found');
        }

        setStoreItem(data);
        
        // Use pricing tiers from API response (already included)
        if (data.pricingTiers && data.pricingTiers.length > 0) {
          const tiers: StoreItemPricingTier[] = data.pricingTiers.map((pt: any) => ({
            storeItemPricingTierId: pt.storeItemPricingTierId,
            storeItemId: pt.storeItemId,
            minQuantity: pt.minQuantity,
            maxQuantity: pt.maxQuantity,
            unitPrice: pt.unitPrice,
            currencyId: pt.currencyId,
            currencyName: pt.currencyName,
            isActive: pt.isActive,
            displayOrder: pt.displayOrder,
            notes: pt.notes
          }));
          setPricingTiers(tiers);
        } else {
          setPricingTiers([]);
        }
        
        // Get image URL
        const defaultImage = data.images?.find(img => img.isDefault) || data.images?.[0];
        const imageUrl = defaultImage?.imageUrl || defaultImage?.imagePath || '';
        
        // Determine stock status
        let stockStatus: 'inStock' | 'limitedStock' | 'outOfStock' = 'inStock';
        if (data.stockQuantity <= 0) {
          stockStatus = 'outOfStock';
        } else if (data.minStockLevel && data.stockQuantity <= data.minStockLevel) {
          stockStatus = 'limitedStock';
        }
        
        // Transform store item to item format
        const transformedItem: Item = {
          id: data.storeItemId || data.itemId || 0,
          name: data.itemName || 'Product',
          nameEn: data.itemNameEn,
          description: data.description || '',
          price: data.price || 0,
          currencyName: data.currencyName,
          image: imageUrl,
          stockStatus: data.isAvailable ? stockStatus : 'outOfStock',
          paymentOption: data.isExternalOrder ? 'partial' : 'full',
          minimumOrderQuantity: data.minimumOrderQuantity || 1,
          isExternalOrder: data.isExternalOrder,
          shippingDetails: data.shippingDetails
        };
        
        setItem(transformedItem);
        setSelectedQuantity(data.minimumOrderQuantity || 1);
      } catch (err) {
        setError(err instanceof Error ? err.message : (t('errorOccurred') as string));
        console.error('Error fetching item details:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchItemDetails();
    }
  }, [id, t]);

  const handleAddToCart = () => {
    if (!item || !user) {
      navigate('/login');
      return;
    }

    // Always use the base/original price - the cart will calculate the price dynamically based on quantity and tiers
    const basePrice = item.price;

    // Convert StoreItemPricingTier to PricingTier format for cart
    const cartPricingTiers = pricingTiers.map(tier => ({
      storeItemPricingTierId: tier.storeItemPricingTierId,
      storeItemId: tier.storeItemId,
      minQuantity: tier.minQuantity,
      maxQuantity: tier.maxQuantity,
      unitPrice: tier.unitPrice,
      isActive: tier.isActive,
      displayOrder: tier.displayOrder
    }));

    // Add item to cart
    addToCart({
      id: item.id,
      name: item.name,
      nameEn: item.nameEn,
      price: basePrice, // Pass base price
      originalPrice: basePrice, // Explicitly pass base price as original
      currencyName: item.currencyName,
      image: item.image,
      pricingTiers: cartPricingTiers.length > 0 ? cartPricingTiers : undefined,
      minimumOrderQuantity: item.minimumOrderQuantity || 1,
      shippingDetails: item.shippingDetails,
      isExternalOrder: item.isExternalOrder
    }, selectedQuantity);

    // Show success message
    Swal.fire({
      icon: 'success',
      title: t('addedToCart') as string,
      text: `${item.name} - ${selectedQuantity} ${t('unit') as string}`,
      confirmButtonText: t('ok') as string,
      timer: 2000,
      timerProgressBar: true
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: item?.name,
        text: item?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      Swal.fire({
        icon: 'success',
        title: t('copied') as string,
        text: t('linkCopied') as string,
        confirmButtonText: t('ok') as string,
        timer: 2000,
        timerProgressBar: true
      });
    }
  };

  const handlePrevImage = () => {
    if (!storeItem?.images || storeItem.images.length === 0) return;
    const newIndex = selectedImageIndex === 0 
      ? storeItem.images.length - 1 
      : selectedImageIndex - 1;
    setImageChanging(true);
    setSelectedImageIndex(newIndex);
    const imgUrl = storeItem.images[newIndex].imageUrl || storeItem.images[newIndex].imagePath || item?.image || '';
    if (item) {
      setItem({ ...item, image: imgUrl });
    }
  };

  const handleNextImage = () => {
    if (!storeItem?.images || storeItem.images.length === 0) return;
    const newIndex = selectedImageIndex === storeItem.images.length - 1 
      ? 0 
      : selectedImageIndex + 1;
    setImageChanging(true);
    setSelectedImageIndex(newIndex);
    const imgUrl = storeItem.images[newIndex].imageUrl || storeItem.images[newIndex].imagePath || item?.image || '';
    if (item) {
      setItem({ ...item, image: imgUrl });
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextImage();
    }
    if (isRightSwipe) {
      handlePrevImage();
    }
  };

  // Calculate price based on quantity and pricing tiers
  const calculatePriceForQuantity = React.useCallback((quantity: number): number | null => {
    if (pricingTiers.length === 0) {
      return item?.price || null;
    }

    // Find the applicable tier for the quantity
    const applicableTier = pricingTiers
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .find(tier => {
        const minQty = tier.minQuantity;
        const maxQty = tier.maxQuantity ?? Infinity;
        return quantity >= minQty && quantity <= maxQty;
      });

    return applicableTier?.unitPrice || item?.price || null;
  }, [pricingTiers, item]);

  // Update calculated price when quantity or tiers change
  useEffect(() => {
    if (selectedQuantity > 0) {
      const price = calculatePriceForQuantity(selectedQuantity);
      setCalculatedPrice(price);
    }
  }, [selectedQuantity, pricingTiers, item, calculatePriceForQuantity]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="item-details-page">
          <Container className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">{t('loading')}</p>
          </Container>
        </div>
      </>
    );
  }

  if (error || !item) {
    return (
      <>
        <Navbar />
        <div className="item-details-page">
          <Container className="text-center py-5">
            <Alert variant="danger">
              <Alert.Heading>{t('error')}</Alert.Heading>
              <p>{error || t('itemNotFound')}</p>
              <Link to="/" className="btn btn-primary">
                {t('backToHome')}
              </Link>
            </Alert>
          </Container>
        </div>
      </>
    );
  }

  const displayName = i18n.language === 'en' && item.nameEn ? item.nameEn : item.name;

  return (
    <>
      <Navbar />
      <div className="item-details-page">
        <Container className="item-details-container">
          {/* Breadcrumb */}
          <Row>
            <Col>
              <nav aria-label="breadcrumb" className="breadcrumb-nav">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/">
                      <i className="fas fa-home me-1"></i>
                      {t('home')}
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {displayName}
                  </li>
                </ol>
              </nav>
            </Col>
          </Row>

          {/* Main Product Section */}
          <Row className="g-4 mb-5">
            {/* Product Images Gallery */}
            <Col lg={6}>
              <div className="product-images-section">
                {storeItem?.images && storeItem.images.length > 0 ? (
                  <div className="noon-style-gallery">
                    {/* Thumbnails - Horizontal Scroll */}
                    {storeItem.images.length > 1 && (
                      <div className="thumbnail-sidebar">
                        {storeItem.images.map((img, index) => {
                          const imgUrl = img.imageUrl || img.imagePath || item.image;
                          const isActive = selectedImageIndex === index;
                          return (
                            <div
                              key={index}
                              className={`thumbnail-item ${isActive ? 'active' : ''}`}
                              onClick={() => {
                                if (selectedImageIndex !== index) {
                                  setImageChanging(true);
                                  setSelectedImageIndex(index);
                                  setItem({ ...item, image: imgUrl });
                                }
                              }}
                            >
                              <img
                                src={imgUrl}
                                alt={`${item.name} - ${index + 1}`}
                                className="thumbnail-img"
                              />
                              {isActive && <div className="thumbnail-active-indicator"></div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Main Image */}
                    <div className="main-image-wrapper">
                      <div 
                        className={`main-image-container-noon ${isImageZoomed ? 'zoomed' : ''} ${imageChanging ? 'changing' : ''}`}
                        onClick={() => {
                          if (!isImageZoomed) {
                            setIsImageZoomed(true);
                            setZoomLevel(1);
                          }
                        }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                      >
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className={`main-product-img ${imageChanging ? 'fade-out' : 'fade-in'}`}
                          style={{ transform: isImageZoomed ? `scale(${zoomLevel})` : 'scale(1)' }}
                          onLoad={() => setImageChanging(false)}
                        />
                        {imageChanging && (
                          <div className="image-loading-overlay">
                            <Spinner animation="border" variant="primary" />
                          </div>
                        )}
                        {!isImageZoomed && (
                          <div className="zoom-hint-noon">
                            <i className="fas fa-search-plus"></i>
                            <span>{t('clickToZoom')}</span>
                          </div>
                        )}
                        
                        {/* Navigation Arrows */}
                        {storeItem.images.length > 1 && !isImageZoomed && (
                          <>
                            <button 
                              className="noon-nav-btn prev-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePrevImage();
                              }}
                            >
                              <i className="fas fa-chevron-right"></i>
                            </button>
                            <button 
                              className="noon-nav-btn next-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNextImage();
                              }}
                            >
                              <i className="fas fa-chevron-left"></i>
                            </button>
                          </>
                        )}
                      </div>
                      
                      {/* Zoom Controls */}
                      {isImageZoomed && (
                        <div className="zoom-controls-noon">
                          <button 
                            className="zoom-btn-noon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setZoomLevel(Math.max(0.5, zoomLevel - 0.25));
                            }}
                          >
                            <i className="fas fa-search-minus"></i>
                          </button>
                          <span className="zoom-level-noon">{Math.round(zoomLevel * 100)}%</span>
                          <button 
                            className="zoom-btn-noon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setZoomLevel(Math.min(3, zoomLevel + 0.25));
                            }}
                          >
                            <i className="fas fa-search-plus"></i>
                          </button>
                          {storeItem.images.length > 1 && (
                            <>
                              <button 
                                className="zoom-btn-noon nav-btn-noon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePrevImage();
                                  setZoomLevel(1);
                                }}
                              >
                                <i className="fas fa-chevron-right"></i>
                              </button>
                              <button 
                                className="zoom-btn-noon nav-btn-noon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNextImage();
                                  setZoomLevel(1);
                                }}
                              >
                                <i className="fas fa-chevron-left"></i>
                              </button>
                            </>
                          )}
                          <button 
                            className="zoom-btn-noon close-btn-noon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsImageZoomed(false);
                              setZoomLevel(1);
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="main-image-wrapper">
                    <div 
                      className={`main-image-container-noon ${isImageZoomed ? 'zoomed' : ''}`}
                      onClick={() => setIsImageZoomed(!isImageZoomed)}
                    >
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="main-product-img"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Video Section - Under Images Gallery */}
              {storeItem?.videoUrl && (
                <div className="product-video-section">
                  <div className="video-header">
                    <h3 className="video-title">
                      <i className="fas fa-video me-2"></i>
                      {t('productVideo')}
                    </h3>
                    <p className="video-subtitle">{t('watchVideoDesc')}</p>
                  </div>
                  <div className="video-wrapper">
                    <iframe
                      src={storeItem.videoUrl.replace('watch?v=', 'embed/')}
                      title={item.name}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              )}
            </Col>
            
            {/* Product Information */}
            <Col lg={6}>
              <div className="product-info-section">
                {/* Title and Badges */}
                <div className="product-header">
                  <h1 className="product-title">{item.name}</h1>
                  <div className="product-badges">
                    {item.stockStatus === 'inStock' && (
                      <Badge bg="success" className="stock-badge">
                        <i className="fas fa-check-circle me-1"></i>
                        {t('inStock')}
                      </Badge>
                    )}
                    {item.stockStatus === 'limitedStock' && (
                      <Badge bg="warning" text="dark" className="stock-badge">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        {t('limitedStock')}
                      </Badge>
                    )}
                    {item.stockStatus === 'outOfStock' && (
                      <Badge bg="danger" className="stock-badge">
                        <i className="fas fa-times-circle me-1"></i>
                        {t('outOfStock')}
                      </Badge>
                    )}
                    {storeItem?.itemCode && (
                      <Badge bg="secondary" className="code-badge">
                        <i className="fas fa-barcode me-1"></i>
                        {storeItem.itemCode}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Price */}
                <div className="price-section">
                  <div className="price-label">{t('priceLabel')}</div>
                  <div className="price-value">
                    {calculatedPrice !== null && pricingTiers.length > 0
                      ? formatPrice(calculatedPrice, item.currencyName)
                      : formatPrice(item.price, item.currencyName)}
                  </div>
                  {pricingTiers.length > 0 && calculatedPrice !== null && calculatedPrice !== item.price && (
                    <div className="original-price">
                      <span className="original-price-text">{t('regularPrice')}: {formatPrice(item.price, item.currencyName)}</span>
                      <span className="discount-badge">
                        {t('discount')} {((((item.price || 0) - calculatedPrice) / (item.price || 1)) * 100).toFixed(0)}%
                      </span>
                    </div>
                  )}
                </div>

                {/* Pricing Tiers Section */}
                {pricingTiers.length > 0 && (
                  <div className="pricing-tiers-section">
                    <h5 className="section-title">
                      <i className="fas fa-tags me-2"></i>
                      {t('quantityPrices')}
                    </h5>
                    <p className="section-subtitle">{t('selectQuantityForBestPrice')}</p>
                    <div className="quantity-input-group">
                      <label className="quantity-label">{t('quantityLabel')}</label>
                      <input
                        type="number"
                        className="quantity-input"
                        min={storeItem?.minimumOrderQuantity || 1}
                        max={storeItem?.stockQuantity || 9999}
                        value={selectedQuantity}
                        onChange={(e) => {
                          const qty = Math.max(storeItem?.minimumOrderQuantity || 1, parseInt(e.target.value) || 1);
                          setSelectedQuantity(qty);
                        }}
                      />
                      <div className="quantity-info">
                        {calculatedPrice !== null && (
                          <div className="calculated-price">
                            <span className="calculated-price-label">{t('unitPriceLabel')}</span>
                            <span className="calculated-price-value">{formatPrice(calculatedPrice, item.currencyName)}</span>
                          </div>
                        )}
                        {calculatedPrice !== null && (
                          <div className="total-price">
                            <span className="total-price-label">{t('totalPriceLabel')}</span>
                            <span className="total-price-value">{formatPrice(calculatedPrice * selectedQuantity, item.currencyName)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="pricing-tiers-table">
                      <div className="pricing-tiers-header">
                        <div className="tier-col-quantity">{t('quantityHeader')}</div>
                        <div className="tier-col-price">{t('unitPriceHeader')}</div>
                        <div className="tier-col-total">{t('totalPriceHeader')}</div>
                      </div>
                      {pricingTiers
                        .sort((a, b) => a.displayOrder - b.displayOrder)
                        .map((tier, index) => {
                          const isSelected = selectedQuantity >= tier.minQuantity && 
                            (tier.maxQuantity === null || selectedQuantity <= tier.maxQuantity);
                          const tierQuantity = selectedQuantity >= tier.minQuantity ? selectedQuantity : tier.minQuantity;
                          return (
                            <div 
                              key={tier.storeItemPricingTierId} 
                              className={`pricing-tier-row ${isSelected ? 'selected' : ''}`}
                              onClick={() => setSelectedQuantity(tier.minQuantity)}
                            >
                              <div className="tier-col-quantity">
                                <span className="tier-quantity-range">
                                  {tier.minQuantity.toFixed(0)}
                                  {tier.maxQuantity ? ` - ${tier.maxQuantity.toFixed(0)}` : '+'}
                                </span>
                                <span className="tier-unit">{t('piece')}</span>
                              </div>
                              <div className="tier-col-price">
                                <span className="tier-unit-price">{formatPrice(tier.unitPrice, tier.currencyName ?? item.currencyName)}</span>
                              </div>
                              <div className="tier-col-total">
                                <span className="tier-total-price">
                                  {formatPrice(tier.unitPrice * tierQuantity, tier.currencyName ?? item.currencyName)}
                                </span>
                                {tier.notes && (
                                  <span className="tier-notes" title={tier.notes}>
                                    <i className="fas fa-info-circle"></i>
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                    <div className="pricing-tiers-footer">
                      <small className="pricing-note">
                        <i className="fas fa-info-circle me-1"></i>
                        {t('quantityDiscountNote')}
                      </small>
                    </div>
                  </div>
                )}

                {/* Stock Information */}
                {storeItem && (
                  <div className="stock-section">
                    <div className="stock-info-card">
                      <div className="stock-info-item">
                        <i className="fas fa-box text-primary"></i>
                        <div>
                          <span className="stock-label">{t('availableQuantity')}</span>
                          <span className="stock-value">{storeItem.stockQuantity} {t('piece')}</span>
                        </div>
                      </div>
                      {storeItem.minStockLevel && (
                        <div className="stock-info-item">
                          <i className="fas fa-exclamation-circle text-warning"></i>
                          <div>
                            <span className="stock-label">{t('minimumLimit')}</span>
                            <span className="stock-value">{storeItem.minStockLevel} {t('piece')}</span>
                          </div>
                        </div>
                      )}
                      {storeItem.minimumOrderQuantity && storeItem.minimumOrderQuantity > 1 && (
                        <div className="stock-info-item">
                          <i className="fas fa-shopping-bag text-info"></i>
                          <div>
                            <span className="stock-label">{t('minOrderQuantityLabel')}</span>
                            <span className="stock-value">{storeItem.minimumOrderQuantity} {t('piece')}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {storeItem.isExternalOrder && (
                      <Alert variant="info" className="external-order-alert">
                        <i className="fas fa-clock me-2"></i>
                        <strong>{t('externalOrderLabel')}</strong> {t('productAvailableWithin')} {storeItem.leadTimeDays || 30} {t('businessDays')}
                      </Alert>
                    )}
                  </div>
                )}

                {/* Description */}
                {item.description && (
                  <div className="description-section">
                    <h5 className="section-title">
                      <i className="fas fa-align-right me-2"></i>
                      {t('description')}
                    </h5>
                    <p className="description-text">{item.description}</p>
                  </div>
                )}

                {/* Specifications */}
                {storeItem?.specifications && storeItem.specifications.length > 0 && (
                  <div className="specifications-section">
                    <h5 className="section-title">
                      <i className="fas fa-list-ul me-2"></i>
                      {t('specifications')}
                    </h5>
                    <div className="specifications-grid">
                      {storeItem.specifications.map((spec, index) => (
                        <div key={index} className="specification-item">
                          <span className="spec-name">{spec.specificationName}:</span>
                          <span className="spec-value">{spec.specificationValue}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Delivery & Payment Info */}
                <div className="info-cards-section">
                  <div className="info-card delivery-card">
                    <i className="fas fa-truck"></i>
                    <div>
                      <strong>{t('delivery')}</strong>
                      <span>
                        {storeItem?.leadTimeDays 
                          ? `${t('within')} ${storeItem.leadTimeDays} ${storeItem.leadTimeDays > 1 ? t('days') : t('day')}`
                          : t('deliveryIn10Days')
                        }
                      </span>
                    </div>
                  </div>
                  {storeItem?.isExternalOrder && (
                    <div className="info-card payment-card">
                      <i className="fas fa-credit-card"></i>
                      <div>
                        <strong>{t('payment')}</strong>
                        <span>{t('pay50Percent')}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="action-buttons">
                  <Button 
                    variant="success" 
                    size="sm" 
                    onClick={handleAddToCart} 
                    className="add-to-cart-btn"
                    disabled={item.stockStatus === 'outOfStock'}
                  >
                    <i className="fas fa-shopping-cart me-2"></i>
                    {t('addToCart')}
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    onClick={handleShare}
                    className="share-btn"
                  >
                    <i className="fas fa-share-alt me-2"></i>
                    {t('shareLink')}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ItemDetails;
